/* INTENT BEFORE EFFECT. An order an agent is about to place is drawn in the same place every time as a short preview: what
   it is, the numbers the order itself carries, and which charted number or which bar it is aimed at, in which direction.
   It is the intent and not a forecast: the world does not say how far or how soon, and the preview draws no such number.
   Only the order's own arguments are printed. The aims are the console's table of what each order is for; its ids are
   the world's own (vocabulary.ts, checked by intent.test.ts).

   After the order is placed, the same place says where the chart has it (awaiting verification, running ...) once the
   chart is read. */
import { describeCall } from '../../lib/schema.ts'
import type { Chart } from '../../lib/types.ts'
import { SEDATIVES, type Param } from './theatre.ts'

export type Dir = 'up' | 'down'
export type Aim = { kind: 'number'; param: Param; dir: Dir } | { kind: 'fluid'; ml: number | null } | { kind: 'sedation' }
export type Intent = { tool: string; headline: string; aims: Aim[]; noAim: boolean }

/** Given by drip to raise the pressure. */
export const PRESSORS = ['norepinephrine', 'epinephrine', 'vasopressin', 'phenylephrine'] as const
/** Given to lower the pressure. */
export const DEPRESSORS = ['nitroglycerin', 'labetalol'] as const
/** Raise the oxygen the patient breathes, or take over the breathing. */
export const OXYGEN = ['nasal_cannula', 'hfnc', 'niv', 'ventilator', 'intubate'] as const
export const FEVER = ['acetaminophen', 'temperature_control'] as const
/** Fluids that go into the circulation: they are aimed at the pressure as well as at the fluid bar. Free water (d5w) is not. */
export const VOLUME = ['ns_bolus', 'lr_bolus', 'albumin_5'] as const

const has = (list: readonly string[], id: unknown): boolean => typeof id === 'string' && list.includes(id)

/** The intent of one call, from its tool and arguments. Reads and questions have none. */
export function intentOf(tool: string, args: Record<string, unknown>): Intent {
  const headline = describeCall(tool, args)
  const aims: Aim[] = []
  if (tool === 'order_fluids') {
    const ml = typeof args.volume_ml === 'number' ? args.volume_ml : null
    if (has(VOLUME, args.fluid)) aims.push({ kind: 'number', param: 'map', dir: 'up' })
    aims.push({ kind: 'fluid', ml })
  } else if (tool === 'order_med') {
    if (has(PRESSORS, args.drug)) aims.push({ kind: 'number', param: 'map', dir: 'up' })
    if (has(DEPRESSORS, args.drug)) aims.push({ kind: 'number', param: 'map', dir: 'down' })
    if (has(FEVER, args.drug)) aims.push({ kind: 'number', param: 'temp', dir: 'down' })
    if (has(SEDATIVES, args.drug)) aims.push({ kind: 'sedation' })
  } else if (tool === 'order_support') {
    if (has(OXYGEN, args.order)) aims.push({ kind: 'number', param: 'spo2', dir: 'up' })
  }
  return { tool, headline, aims, noAim: aims.length === 0 }
}

/** The sentence under the preview: where the aim points, in words, never a size or a time. */
export function aimText(a: Aim): string {
  if (a.kind === 'fluid') return a.ml === null ? 'adds to the fluid bar' : `adds ${a.ml} mL to the fluid bar`
  if (a.kind === 'sedation') return 'adds one infusion to the sedation bar'
  const name = { hr: 'HR', map: 'MAP', spo2: 'SpO2', rr: 'RR', temp: 'temperature', mental: 'level of consciousness' }[a.param]
  return `aimed at ${name}, ${a.dir === 'up' ? 'up' : 'down'}`
}

/** An order the agent placed, found again on the chart by its tool and arguments, newest first; the chart lists its status once the agent reads the orders. */
export function placedStatus(tool: string, args: Record<string, unknown>, orders: Chart['orders']): string | null {
  const id = tool === 'order_fluids' ? args.fluid : tool === 'order_med' ? args.drug : tool === 'order_lab' ? args.test : tool === 'order_procedure' ? args.procedure : tool === 'order_support' ? args.order : null
  if (typeof id !== 'string') return null
  const found = [...orders].reverse().find((o) => o.order === id)
  return found ? found.status.replace(/_/g, ' ') : null
}
